import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, TenantData } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button, Label, TextInput, Alert, Modal, Select, Spinner } from 'flowbite-react';
import { HiUser, HiPhone, HiCheckCircle, HiInformationCircle, HiLocationMarker, HiHome, HiUserGroup, HiOfficeBuilding } from 'react-icons/hi';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para los iconos de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Componente para actualizar el mapa
function MapUpdater({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

// Componente para manejar clicks en el mapa
function MapClickHandler({ onMapClick }: { onMapClick: (e: L.LeafletMouseEvent) => void }) {
  useMapEvents({ click: onMapClick });
  return null;
}

// Función para capitalizar texto
const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [step, setStep] = useState(1); // 1: Datos básicos, 2: Información adicional si corresponde
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    userType: 'personal' as 'personal' | 'familiar' | 'comunidad' | 'empresa',
  });
  const [tenantData, setTenantData] = useState<TenantData>({
    name: '',
    address: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([-33.4489, -70.6693]);
  const [mapZoom, setMapZoom] = useState(10);
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const geocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastGeocodedAddressRef = useRef<string>('');

  // Geocodificar dirección automáticamente
  const geocodeAddress = useCallback(async (address: string) => {
    if (!address.trim() || address.trim().length < 5) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            'User-Agent': 'DomusSecuraApp/1.0 (haliagav@gmail.com)',
          },
        }
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newLat = parseFloat(lat);
        const newLon = parseFloat(lon);
        setMapCenter([newLat, newLon]);
        setMarkerPosition([newLat, newLon]);
        setTenantData(prev => ({ ...prev, latitude: lat, longitude: lon }));
        setMapZoom(17);
        lastGeocodedAddressRef.current = address;
      }
    } catch (error) {
      console.error('Error geocodificando:', error);
    }
  }, []);

  // Efecto para geocodificar automáticamente con debounce
  useEffect(() => {
    if (geocodeTimeoutRef.current) {
      clearTimeout(geocodeTimeoutRef.current);
    }

    const addressTrimmed = tenantData.address?.trim() || '';
    if (step === 2 && formData.userType !== 'personal' && addressTrimmed.length > 5 && addressTrimmed !== lastGeocodedAddressRef.current) {
      geocodeTimeoutRef.current = setTimeout(() => {
        geocodeAddress(addressTrimmed);
      }, 1000);
    }

    return () => {
      if (geocodeTimeoutRef.current) {
        clearTimeout(geocodeTimeoutRef.current);
      }
    };
  }, [tenantData.address, step, formData.userType, geocodeAddress]);

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    setMarkerPosition([lat, lng]);
    setMapCenter([lat, lng]);
    setTenantData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
  };

  useEffect(() => {
    const checkProfile = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      // Obtener perfil actual (incluyendo user_type)
      const { data: profile } = await supabase
        .from('users')
        .select('name, phone, user_type')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        // Si ya tiene nombre Y user_type, redirigir
        if (profile.name && profile.user_type) {
          await refreshUserProfile();
          if (userProfile?.status === 'active') {
            navigate('/dashboard');
          } else {
            navigate('/waiting-approval');
          }
          return;
        }

        // Prellenar con datos existentes
        setFormData({
          name: profile.name || '',
          phone: profile.phone || '',
          userType: (profile.user_type as any) || 'personal',
        });
      } else {
        // Si no existe perfil, obtener datos de auth (Google)
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          setFormData({
            name: authUser.user_metadata?.full_name || 
                  authUser.user_metadata?.name || 
                  authUser.email?.split('@')[0] || 
                  '',
            phone: authUser.user_metadata?.phone || '',
            userType: 'personal',
          });
        }
      }

      setChecking(false);
    };

    checkProfile();
  }, [user, navigate, refreshUserProfile, userProfile]);

  const handleContinueStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('El nombre es requerido');
      return;
    }

    // Si el tipo NO es personal, ir al paso 2
    if (formData.userType !== 'personal') {
      setStep(2);
    } else {
      // Si es personal, completar perfil directamente
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    if (!user) {
      setError('No hay usuario autenticado');
      setLoading(false);
      return;
    }

    try {
      // Determinar rol según tipo de usuario
      let role: 'superadmin' | 'admin' | 'user' = 'user';
      if (formData.userType === 'familiar' || formData.userType === 'comunidad' || formData.userType === 'empresa') {
        role = 'admin';
      }

      // Obtener datos del usuario de Google Auth
      const { data: { user: authUser } } = await supabase.auth.getUser();

      // Verificar si el perfil existe
      const { data: existingProfile } = await supabase
        .from('users')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (existingProfile) {
        // Actualizar perfil existente
        const { error: updateError } = await supabase
          .from('users')
          .update({
            name: capitalizeWords(formData.name.trim()),
            phone: formData.phone.trim() || null,
            user_type: formData.userType,
            role,
          })
          .eq('id', user.id);

        if (updateError) throw updateError;
      } else {
        // Crear nuevo perfil
        const { error: insertError } = await supabase
          .from('users')
          .insert({
            id: user.id,
            email: user.email?.toLowerCase().trim() || '',
            name: capitalizeWords(formData.name.trim()),
            phone: formData.phone.trim() || null,
            auth_provider: 'google',
            google_id: authUser?.app_metadata?.provider_id,
            user_type: formData.userType,
            role,
            status: 'pending_approval',
          });

        if (insertError) throw insertError;
      }

      // Si hay datos de tenant y el tipo no es personal, crear tenant y asociarlo
      if (tenantData.name && formData.userType !== 'personal') {
        try {
          // Crear tenant
          const { data: newTenant, error: tenantError } = await supabase
            .from('tenants')
            .insert({
              uuid: crypto.randomUUID(),
              name: capitalizeWords(tenantData.name),
              address: tenantData.address ? capitalizeWords(tenantData.address) : null,
              description: tenantData.description ? capitalizeWords(tenantData.description) : null,
              type: formData.userType, // El tipo del tenant coincide con el tipo del usuario
              latitude: tenantData.latitude || null,
              longitude: tenantData.longitude || null,
              status: 'active',
              is_active: true,
            })
            .select()
            .single();

          if (tenantError) {
            console.error('Error creating tenant:', tenantError);
            throw new Error('Error al crear el tenant: ' + tenantError.message);
          }

          if (newTenant) {
            // Crear relación usuario-tenant
            const { error: relationError } = await supabase
              .from('user_tenants')
              .insert({
                user_id: user.id,
                tenant_id: newTenant.id,
              });

            if (relationError) {
              console.error('Error creating user-tenant relation:', relationError);
              // No fallar completamente, pero loguear el error
            } else {
              // Actualizar también el campo legacy tenant_id para compatibilidad
              await supabase
                .from('users')
                .update({ tenant_id: newTenant.id })
                .eq('id', user.id);
            }
          }
        } catch (tenantError: any) {
          console.error('Error in tenant creation process:', tenantError);
          // No fallar completamente si falla la creación del tenant
          // El usuario puede continuar y el admin puede crear el tenant manualmente
        }
      }

      // Refrescar perfil y mostrar éxito
      await refreshUserProfile();
      setShowSuccessModal(true);
      setLoading(false);

      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/waiting-approval');
      }, 3000);
    } catch (err: any) {
      console.error('Error completing profile:', err);
      setError(err.message || 'Error al completar el perfil');
      setLoading(false);
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setError('');
  };

  const getTypeDescription = (type: string) => {
    switch (type) {
      case 'personal':
        return 'Uso personal e individual';
      case 'familiar':
        return 'Para tu familia o grupo pequeño (obtienes rol de administrador)';
      case 'comunidad':
        return 'Para comunidades, condominios o edificios (obtienes rol de administrador)';
      case 'empresa':
        return 'Para empresas u organizaciones (obtienes rol de administrador)';
      default:
        return '';
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Spinner size="xl" className="text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-12">
      <div className="w-full max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="Domus Secura" 
                className="h-16 w-16 object-contain bg-white rounded-lg p-2 shadow-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/logo.svg';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Completa tu Perfil</h1>
            <p className="text-blue-100">Termina de configurar tu cuenta en Domus Secura</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Indicador de pasos */}
            {formData.userType !== 'personal' && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 1 ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  <span className="font-semibold">1</span>
                  <span className="text-sm">Datos Personales</span>
                </div>
                <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600"></div>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === 2 ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  <span className="font-semibold">2</span>
                  <span className="text-sm">Información {formData.userType === 'familiar' ? 'Familiar' : formData.userType === 'comunidad' ? 'de la Comunidad' : 'de la Empresa'}</span>
                </div>
              </div>
            )}

            {error && (
              <Alert color="failure" className="mb-4">
                <span className="font-medium">{error}</span>
              </Alert>
            )}

            {/* Paso 1: Datos Básicos */}
            {step === 1 && (
              <form onSubmit={handleContinueStep1} className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p className="font-semibold mb-1">Selecciona tu tipo de cuenta</p>
                      <p>El tipo determina las funcionalidades y permisos que tendrás. Si eliges Familiar, Comunidad o Empresa, obtendrás permisos de administrador automáticamente.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="userType">Tipo de Cuenta</Label>
                  <Select
                    id="userType"
                    value={formData.userType}
                    onChange={(e) => setFormData({ ...formData, userType: e.target.value as any })}
                    className="mt-1"
                    required
                  >
                    <option value="personal">👤 Personal</option>
                    <option value="familiar">🏠 Familiar</option>
                    <option value="comunidad">👥 Comunidad</option>
                    <option value="empresa">🏢 Empresa</option>
                  </Select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {getTypeDescription(formData.userType)}
                  </p>
                </div>

                <div>
                  <Label htmlFor="name">Nombre Completo</Label>
                  <TextInput
                    id="name"
                    type="text"
                    icon={HiUser}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: capitalizeWords(e.target.value) })}
                    required
                    placeholder="Juan Pérez"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Teléfono <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="phone"
                    type="tel"
                    icon={HiPhone}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+56912345678"
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {formData.userType === 'personal' ? (
                    loading ? 'Completando...' : 'Completar Registro'
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </form>
            )}

            {/* Paso 2: Información del Tenant */}
            {step === 2 && (
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <HiInformationCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p className="font-semibold mb-1">
                        {formData.userType === 'familiar' || formData.userType === 'comunidad' || formData.userType === 'empresa' 
                          ? '¡Importante! Obtendrás permisos de administrador automáticamente.' 
                          : 'Completa la información de tu tenant'}
                      </p>
                      <p>Esta información se usará para crear tu espacio en la plataforma. Podrás editarla después.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="tenantName">
                    Nombre {formData.userType === 'familiar' ? 'del Grupo Familiar' : formData.userType === 'comunidad' ? 'de la Comunidad' : 'de la Empresa'}
                  </Label>
                  <TextInput
                    id="tenantName"
                    type="text"
                    icon={formData.userType === 'familiar' ? HiHome : formData.userType === 'comunidad' ? HiUserGroup : HiOfficeBuilding}
                    value={tenantData.name}
                    onChange={(e) => setTenantData({ ...tenantData, name: capitalizeWords(e.target.value) })}
                    required
                    placeholder={formData.userType === 'familiar' ? 'Ej: Familia Pérez' : formData.userType === 'comunidad' ? 'Ej: Condominio Las Flores' : 'Ej: Empresa XYZ Ltda.'}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="tenantAddress">Dirección <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="tenantAddress"
                    type="text"
                    icon={HiLocationMarker}
                    value={tenantData.address || ''}
                    onChange={(e) => setTenantData({ ...tenantData, address: capitalizeWords(e.target.value) })}
                    placeholder="Ej: Av. Principal 123, Santiago"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    El mapa se actualizará automáticamente mientras escribes
                  </p>
                </div>

                {tenantData.address && (
                  <div className="h-64 w-full rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <MapContainer
                      center={mapCenter}
                      zoom={mapZoom}
                      style={{ height: '100%', width: '100%' }}
                      whenReady={() => {
                        // El mapa se ajustará automáticamente
                      }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      {markerPosition && (
                        <Marker position={markerPosition} draggable={true} />
                      )}
                      <MapUpdater center={mapCenter} zoom={mapZoom} />
                      <MapClickHandler onMapClick={handleMapClick} />
                    </MapContainer>
                  </div>
                )}

                <div>
                  <Label htmlFor="tenantDescription">Descripción <span className="text-gray-400 dark:text-gray-500 text-sm">(opcional)</span></Label>
                  <TextInput
                    id="tenantDescription"
                    type="text"
                    icon={HiInformationCircle}
                    value={tenantData.description || ''}
                    onChange={(e) => setTenantData({ ...tenantData, description: capitalizeWords(e.target.value) })}
                    placeholder={`Información adicional sobre tu ${formData.userType === 'familiar' ? 'grupo familiar' : formData.userType === 'comunidad' ? 'comunidad' : 'empresa'}`}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    color="gray"
                    onClick={handleBackToStep1}
                    className="flex-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                    disabled={loading}
                  >
                    Atrás
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !tenantData.name.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Completando...
                      </>
                    ) : (
                      'Completar Registro'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Éxito */}
      <Modal show={showSuccessModal} onClose={() => {}} size="md">
        <div className="p-6 bg-white dark:bg-gray-800 text-center">
          <HiCheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
          <h3 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
            ¡Perfil Completado!
          </h3>
          <div className="space-y-3 mb-5">
            <p className="text-base text-gray-500 dark:text-gray-400">
              Tu perfil ha sido completado exitosamente.
            </p>
            {(formData.userType === 'familiar' || formData.userType === 'comunidad' || formData.userType === 'empresa') && (
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-300 font-semibold">
                  ✅ Obtuviste permisos de administrador automáticamente
                </p>
              </div>
            )}
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <HiInformationCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-left">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                  Próximos pasos:
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Espera la aprobación del administrador</li>
                  <li>• Te notificaremos cuando tu cuenta sea activada</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirigiendo a la pantalla de espera...
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
